// src/components/common/ConfirmDialog.tsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export type ConfirmDialogType = 'delete' | 'warning' | 'info' | 'custom';

export interface ConfirmDialogProps {
  isOpen: boolean;
  type: ConfirmDialogType;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /**
   * true 時把「主要醒目按鈕」的位置與樣式給 cancel：
   * cancel 渲染在右邊並套醒目色（成為預設選項），confirm 渲染在左邊並套 ghost。
   * 用於危險或我們不鼓勵的操作（如跳過教學）——confirm 仍是那個危險動作，
   * 但視覺上不作為預設選項，避免誤觸。呼叫端語意不變（confirm 仍回傳 true）。
   */
  cancelIsPrimary?: boolean;
  requiresInput?: boolean;
  inputPlaceholder?: string;
  inputLabel?: string;
  onConfirm: (inputValue?: string) => void;
  onCancel: () => void;
}

// 圖標組件
const DialogIcon: React.FC<{ type: ConfirmDialogType }> = ({ type }) => {
  const iconProps = {
    className: "w-6 h-6",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24",
    strokeWidth: 2
  };

  switch (type) {
    case 'delete':
      return (
        <svg {...iconProps}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...iconProps}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    case 'info':
      return (
        <svg {...iconProps}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return (
        <svg {...iconProps}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

// 主要 ConfirmDialog 組件
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  type,
  title,
  message,
  confirmText,
  cancelText = "Cancel",
  cancelIsPrimary = false,
  requiresInput = false,
  inputPlaceholder = "",
  inputLabel = "",
  onConfirm,
  onCancel
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 類型樣式配置
  const typeStyles = {
    delete: {
      icon: 'text-error-500 bg-error-50',
      confirmButton: 'bg-error-500 hover:bg-error-600 focus:ring-error-200 text-white',
      defaultConfirmText: 'Delete'
    },
    warning: {
      icon: 'text-warning-500 bg-warning-50',
      confirmButton: 'bg-warning-500 hover:bg-warning-600 focus:ring-warning-200 text-white',
      defaultConfirmText: 'Continue'
    },
    info: {
      icon: 'text-sky-500 bg-sky-50',
      confirmButton: 'bg-sky-500 hover:bg-sky-600 focus:ring-sky-200 text-white',
      defaultConfirmText: 'Confirm'
    },
    custom: {
      icon: 'text-neutral-500 bg-neutral-50',
      confirmButton: 'bg-neutral-500 hover:bg-neutral-600 focus:ring-neutral-200 text-white',
      defaultConfirmText: 'OK'
    }
  };

  const styles = typeStyles[type];
  const finalConfirmText = confirmText || styles.defaultConfirmText;

  // 動畫控制
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsLeaving(false);
      setInputValue(""); // 重置輸入值
      // 如果需要輸入，聚焦到輸入框；否則聚焦到取消按鈕
      setTimeout(() => {
        if (requiresInput) {
          inputRef.current?.focus();
        } else {
          cancelButtonRef.current?.focus();
        }
      }, 100);
    } else {
      setIsLeaving(true);
      setTimeout(() => setIsVisible(false), 200);
    }
  }, [isOpen, requiresInput]);

  // 鍵盤事件處理
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onCancel();
          break;
        case 'Enter':
          // 如果需要輸入且焦點在輸入框，不要自動提交
          if (requiresInput && document.activeElement === inputRef.current) {
            return;
          }
          event.preventDefault();
          onConfirm(requiresInput ? inputValue : undefined);
          break;
        case 'Tab':
          // 處理 Tab 鍵循環焦點
          const activeElement = document.activeElement;
          if (requiresInput) {
            if (activeElement === inputRef.current) {
              event.preventDefault();
              confirmButtonRef.current?.focus();
            } else if (activeElement === confirmButtonRef.current) {
              event.preventDefault();
              cancelButtonRef.current?.focus();
            } else if (activeElement === cancelButtonRef.current) {
              event.preventDefault();
              inputRef.current?.focus();
            }
          } else {
            // 在兩個按鈕之間循環
            if (activeElement === confirmButtonRef.current) {
              event.preventDefault();
              cancelButtonRef.current?.focus();
            } else if (activeElement === cancelButtonRef.current) {
              event.preventDefault();
              confirmButtonRef.current?.focus();
            }
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onConfirm, onCancel, requiresInput, inputValue]);

  if (!isVisible) return null;

  const backdropClasses = isLeaving
    ? 'animate-fadeOut'
    : 'animate-fadeIn';

  const dialogClasses = isLeaving
    ? 'animate-fadeOut scale-95'
    : 'animate-fadeIn scale-100';

  return createPortal(
    <div
      className={`
        fixed inset-0 z-modal-nested
        flex items-center justify-center
        bg-black bg-opacity-50 backdrop-blur-sm
        px-4 py-6
        ${backdropClasses}
      `}
      onClick={onCancel}
    >
      <div
        className={`
          bg-white rounded-2xl shadow-2xl
          max-w-sm w-full mx-4
          transform transition-all duration-200 ease-out
          ${dialogClasses}
        `}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
      >
        {/* 內容區域 */}
        <div className="p-6 text-center">
          {/* 圖標 */}
          <div className={`
            w-12 h-12 mx-auto mb-4 rounded-full 
            flex items-center justify-center
            ${styles.icon}
          `}>
            <DialogIcon type={type} />
          </div>

          {/* 標題 */}
          <h3 
            id="confirm-dialog-title"
            className="text-lg font-semibold text-neutral-900 mb-2"
          >
            {title}
          </h3>

          {/* 訊息 */}
          <p
            id="confirm-dialog-message"
            className="text-sm text-neutral-600 mb-6 leading-relaxed whitespace-pre-line"
          >
            {message}
          </p>

          {/* 輸入框（如果需要） */}
          {requiresInput && (
            <div className="mb-6 text-left">
              {inputLabel && (
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {inputLabel}
                </label>
              )}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={inputPlaceholder}
                className="
                  w-full px-3 py-2
                  border border-neutral-300 rounded-lg
                  text-sm text-neutral-900
                  placeholder-neutral-400
                  focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent
                  transition-colors duration-150
                "
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && inputValue.trim()) {
                    onConfirm(inputValue);
                  }
                }}
              />
            </div>
          )}

          {/*
            按鈕區域。
            預設：confirm 在右邊套醒目色（主要選項），cancel 在左邊套 ghost（次要）。
            cancelIsPrimary 時對調視覺主次：cancel 移到右邊套醒目色成為預設選項，
            confirm 移到左邊套 ghost。用 flex-row-reverse 對調「視覺位置」，
            DOM 順序不動（保持 cancel→confirm，Tab 與 ref 邏輯不受影響）。
          */}
          <div className={`flex space-x-3 ${cancelIsPrimary ? 'flex-row-reverse space-x-reverse' : ''}`}>
            {/* 取消按鈕：cancelIsPrimary 時套醒目色成為主要選項 */}
            <button
              ref={cancelButtonRef}
              onClick={onCancel}
              className={
                cancelIsPrimary
                  ? `flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.confirmButton}`
                  : 'btn-ghost flex-1 py-2.5 rounded-xl'
              }
            >
              {cancelText}
            </button>

            {/* 確認按鈕：cancelIsPrimary 時降為 ghost 次要選項（仍是那個危險動作） */}
            <button
              ref={confirmButtonRef}
              onClick={() => onConfirm(requiresInput ? inputValue : undefined)}
              disabled={requiresInput && !inputValue.trim()}
              className={
                cancelIsPrimary
                  ? 'btn-ghost flex-1 py-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed'
                  : `flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${styles.confirmButton}`
              }
            >
              {finalConfirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;